import { useState } from "react";
import { Loader2, AlertTriangle, Upload, GitCompare, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import FrsSyncDialog from "./FrsSyncDialog";

const NEEDS_NOTE = { process_unapproved: true, revise_resubmit: true, rejected: true, void: true };
const DOC_KIND = { process_unapproved: "supporting", process_approved: "executed" };

/** The status machine, with its requirements made obvious rather than hidden in an error. */
export default function CoStatusDialog({ projectId, co, target, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [frsReason, setFrsReason] = useState("");
  const [docId, setDocId] = useState(
    target.status === "process_approved" ? co.executed_document_id || ""
      : co.authorization_document_id || "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const frsState = co.frs_sync?.state || "not_reviewed";
  const frsDone = ["synced", "not_applicable", "overridden"].includes(frsState);
  const needsFrs = ["process_approved", "process_unapproved"].includes(target.status) && !frsDone;
  const [frsOpen, setFrsOpen] = useState(false);

  const noFrsChange = async () => {
    setBusy(true);
    try {
      const res = await opsApi.coFrsSync(projectId, co.id, {
        mode: "not_applicable", note: `No FRS change — confirmed at ${target.label}` });
      toast.success("Recorded: this change order does not change the FRS");
      onDone(res.change_order);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record that"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("files", file);
      form.append("kind", DOC_KIND[target.status] || "supporting");
      const res = await opsApi.coUploadDocument(projectId, co.id, form);
      setDocId(res.documents[0].id);
      toast.success(`${res.documents[0].file_name} filed to Documents / Change Orders`);
    } catch { toast.error("That file could not be filed"); }
    finally { setBusy(false); }
  };

  const go = async (confirm = false) => {
    setBusy(true);
    setProblem(null);
    try {
      const res = await opsApi.coStatus(projectId, co.id, {
        status: target.status, note, document_id: docId, confirm,
        frs_override_reason: frsReason,
      });
      (res.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`${co.number} is now ${target.label}`);
      onDone(res.change_order);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") setProblem(d);
      else toast.error(d || "That status change was refused");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-border bg-card text-card-foreground p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-co-status-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          {co.number} → {target.label}
        </h3>
        <p className="mt-1 text-xs text-slate-500">{target.meaning}</p>

        {target.status === "process_unapproved" && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Processed Unapproved writes to the <b>FRS</b>, releases <b>procurement</b> and does <b>not</b> release
            billing. Record who authorised the work and attach what they sent.
          </div>
        )}
        {target.status === "process_approved" && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            This is the only status that writes to the budget, the contract and the schedule of
            values. A budget snapshot is taken first.
          </div>
        )}

        {needsFrs && target.status === "process_unapproved" && (
          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200"
               data-testid="ops-co-status-frs-push">
            <p className="flex items-center gap-1.5 font-medium">
              <GitCompare className="h-3.5 w-3.5" /> Processing pushes this change order to the FRS
            </p>
            <p className="mt-1">Every line with a room is written to the Current FRS as a revision stamped {co.number}.
              Lines without a room raise an FRS Update task — place them first if you want them on the FRS now.</p>
            <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" disabled={busy} onClick={() => setFrsOpen(true)}
                    data-testid="ops-co-status-frs-review">Review / place rooms first</Button>
          </div>
        )}
        {needsFrs && target.status === "process_approved" && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200"
               data-testid="ops-co-status-frs-gate">
            <p className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> The FRS has not been updated for this change order
            </p>
            <p className="mt-1">Does {co.number} add, remove or change anything in a room?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => setFrsOpen(true)}
                      data-testid="ops-co-status-frs-yes">
                <GitCompare className="mr-1.5 h-3.5 w-3.5" /> Yes — update the FRS now
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={noFrsChange}
                      data-testid="ops-co-status-frs-no">No — nothing changes in the FRS</Button>
            </div>
            {target.status === "process_approved" && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] opacity-80">Override instead (logged)</summary>
                <Textarea value={frsReason} onChange={(e) => setFrsReason(e.target.value)} rows={2}
                          className="mt-1 text-xs" placeholder="Why the FRS review is being skipped"
                          data-testid="ops-co-status-frs-reason" />
              </details>
            )}
          </div>
        )}
        {!needsFrs && ["process_approved", "process_unapproved"].includes(target.status) && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300" data-testid="ops-co-status-frs-ok">
            <Check className="h-3.5 w-3.5" />
            {frsState === "synced" ? "FRS updated for this change order"
              : frsState === "not_applicable" ? "Recorded: no FRS change for this change order"
                : "FRS review overridden"}
          </p>
        )}

        <label className="mt-3 block text-xs font-medium text-slate-600">
          Note {NEEDS_NOTE[target.status] ? "(required)" : "(optional)"}
        </label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1"
                  data-testid="ops-co-status-note"
                  placeholder={target.status === "process_unapproved"
                    ? "e.g. Dave at Swinerton emailed authorization to proceed on 12 June"
                    : "What happened"} />

        {DOC_KIND[target.status] && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-slate-600">
              {target.status === "process_approved" ? "Executed change order" : "Supporting document"}
              {target.status === "process_unapproved" ? " (required)" : ""}
            </label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:border-slate-400">
              <Upload className="h-3.5 w-3.5" />
              {docId ? "Filed — choose another to replace it" : "Choose a file"}
              <input type="file" className="hidden" data-testid="ops-co-status-file"
                     onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
        )}

        {problem && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
               data-testid="ops-co-status-problem">
            {problem.detail}
            {problem.missing && <span> — missing: {problem.missing.join(", ")}</span>}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          {problem?.confirmable && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => go(true)}
                    data-testid="ops-co-status-confirm">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Continue anyway
            </Button>
          )}
          <Button size="sm" disabled={busy} onClick={() => go(false)}
                  data-testid="ops-co-status-apply">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} {target.label}
          </Button>
        </div>
      </div>
      {frsOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <FrsSyncDialog projectId={projectId} co={co} onClose={() => setFrsOpen(false)}
                         onDone={(c) => { onDone(c); }} />
        </div>
      )}
    </div>
  );
}
